import { createRef, ReactElement, useEffect, useRef, useState } from 'react';
import SlotDate from '@molecules/SlotDate/SlotDate';
import { dateToNumeric, dateToShortMonth, getDayName, range } from '@helpers/date';
import { useTranslations } from 'next-intl';
import type { AxfoodSlotsScheduleViewModel, AxfoodTimeSlotViewModel } from '@occ/api-client';
import { StyledSlotDateCarousel } from './SlotDatePicker.styles';
import { useMediaQuery } from 'react-responsive';
import Config from '@config';

interface Props {
  slotData: AxfoodSlotsScheduleViewModel;
  onSelectDate: (date: DayDate | null, preselect: boolean) => void;
  selectedDate: DayDate | null;
  daysAhead: number;
}

const SlotDatePicker = ({ slotData, daysAhead, onSelectDate, selectedDate }: Props) => {
  const dayDateRefs = useRef([]);
  const [datesElement, setDatesElement] = useState<Array<ReactElement>>([]);
  const [slideToPage, setSlideToPage] = useState(0);
  const t = useTranslations('slotSelector');

  const isTouch = useMediaQuery({
    query: Config.DEVICE.IS_TOUCH,
  });

  const getSlotDays = (): Array<DayDate> => {
    const slotRange = range(slotData.startDate, slotData.endDate);
    const daysRange = slotRange.getDaysArray();

    let index = 0;
    return daysRange.map((day) => {
      const dayData: DayDate = {
        groupKey: dateToNumeric(day),
        dayName: index === 0 ? t('today') : index === 1 ? t('tomorrow') : getDayName(day),
        dateText: dateToShortMonth(day),
      };
      index += 1;
      return dayData;
    });
  };

  const dates = getSlotDays();

  const getSlotDaysElements = (days: Array<DayDate>): Array<ReactElement> => {
    return days.map((day, i) => {
      return (
        <SlotDate
          key={`${day.groupKey}-${i}`}
          onClick={(date: DayDate) => {
            onSelectDate(date, false);
          }}
          isSelected={selectedDate?.groupKey === day?.groupKey}
          date={day}
          ref={(dayDateRefs.current[i] = dayDateRefs.current[i] || createRef())}
        />
      );
    });
  };

  const getIndexOfDayDate = (dayDate: DayDate | null) => {
    if (dayDate) {
      for (let i = 0; i < dates.length; i += 1) {
        const day = dates[i];
        if (dayDate.groupKey === day.groupKey) return i;
      }
    }

    return 0;
  };

  const getDayDateForSlot = (slot: AxfoodTimeSlotViewModel): DayDate | null => {
    let selectedDay = null;
    for (const day of dates) {
      if (day.groupKey === dateToNumeric(slot.startTime || 0)) {
        selectedDay = day;
        break;
      }
    }
    return selectedDay;
  };

  const getSelectedSlotDate = (data: AxfoodSlotsScheduleViewModel): DayDate | null => {
    if (data.selectedSlot) {
      return getDayDateForSlot(data.selectedSlot);
    }

    if (data?.slots) {
      for (const slot of data.slots) {
        if (slot?.available) {
          return getDayDateForSlot(slot);
        }
      }
    }
    return null;
  };

  useEffect(() => {
    setDatesElement(getSlotDaysElements(dates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    // Preselect the date
    const selectedSlotDate = getSelectedSlotDate(slotData);
    onSelectDate(selectedSlotDate, true);
    setSlideToPage(parseInt(String(getIndexOfDayDate(selectedSlotDate) / daysAhead), 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {dates && datesElement.length > 0 && (
        <StyledSlotDateCarousel
          $isTouch={isTouch}
          data-testid="slot-date-picker"
          slideToPage={slideToPage}
          elements={datesElement}
          elementsPerSlide={daysAhead}
          variant="slotpicker"
        />
      )}
    </>
  );
};

export default SlotDatePicker;
